import { z } from "zod";

import {
  genericRegistryItemMetadataSchema,
  registryContentFilesSchema,
  registryItemIdSchema,
  registryItemTypeSchema,
  registryItemVisibilitySchema,
  registrySlugSchema,
  validateRegistryAppRoot,
} from "./registryCatalog";

// NOTE (Cole, 2026-05-01): I want to rename this to RegistryItem, but that type is already taken.
// I'll combine this on a future pr.
//
// `previewHtml` is intentionally NOT on the listing/metadata read shape — it's
// a heavy rendered HTML blob (tens to hundreds of KB per row). Listing
// responses ship many rows and can't afford that bloat. Callers that need
// `previewHtml` for a single item should call the DB-backed single-item read
// (`registry.get({ id })` / `GET /items/:id`), which returns the
// `RegistryItemDetail` shape below.
export const registryItemMetadataSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    type: registryItemTypeSchema,
    metadata: genericRegistryItemMetadataSchema.nullable(),
    projectId: z.string().nullable(),
    createdBy: z.string().nullable(),
    sourceType: z.string().nullable(),
    sourceKey: z.string().nullable(),
    sourceSitePageId: z.string().nullish(),
    shortLink: z.string().nullable(),
    description: z.string().nullable(),
    visibility: z.string(),
    tags: z.array(z.string()),
    category: z.string().nullable(),
    previewUrls: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("RegistryItemMetadata");

export const registryItemCategoriesResultSchema = z
  .object({
    categories: z.array(z.string()),
  })
  .describe("RegistryItemCategoriesResult");

export const registryItemTagsResultSchema = z
  .object({
    tags: z.array(z.string()),
  })
  .describe("RegistryItemTagsResult");

// Single-item DB read shape: listing metadata plus the rendered `previewHtml`
// for preview surfaces (template cards, modals, admin row detail). The R2
// document is not consulted on this path; `previewHtml` lives on the
// Postgres row.
export const registryItemDetailSchema = registryItemMetadataSchema
  .extend({
    previewHtml: z.string().nullable(),
  })
  .describe("RegistryItemDetail");

// Public, unauthenticated preview read. Identity attribution (`projectId` /
// `createdBy`) is always stripped to null server-side before returning, so
// the schema pins both to `z.null()` to enforce the invariant at the
// response boundary.
//
// `body` powers the app-like skill/plan share view: it is the
// frontmatter-stripped markdown of the primary instructions file, read
// best-effort and only populated for skill/plan items (null for everything
// else).
export const registryItemPreviewResultSchema = z
  .object({
    item: registryItemDetailSchema.extend({
      projectId: z.null(),
      createdBy: z.null(),
      body: z.string().nullable(),
    }),
  })
  .describe("RegistryItemPreviewResult");

// R2-backed content for a single registry item. Returned by the explicit
// content endpoint and used by install / edit / download flows that need the
// file tree and npm dependencies. `previewHtml` is intentionally NOT on this
// shape — it's a DB column, served by `registry.get({ id })` instead.
export const registryItemContentSchema = z
  .object({
    meta: z.object({
      id: z.string(),
      slug: z.string(),
      type: registryItemTypeSchema,
      name: z.string(),
      description: z.string().nullable(),
    }),
    files: z.record(z.string(), z.string()),
    dependencies: z.array(z.string()),
  })
  .describe("RegistryItemContent");

export const registryListItemsResultSchema = z
  .object({
    items: z.array(registryItemMetadataSchema),
    nextCursor: z.string().nullable(),
    contentByItemId: z.record(z.string(), registryItemContentSchema).optional(),
    failedContentItemIds: z.array(z.string()).optional(),
  })
  .describe("FindRegistryItemsResult");

// The full self-describing registry document: the `.registry.json` shape
// stored in R2 and the body accepted by the admin document upsert/import
// routes. This is the durable source-of-truth shape; PG rows are a projection
// of it.
export const registryItemDocumentSchema = z
  .object({
    id: z.uuid(),
    slug: z.string().min(1),
    name: z.string().min(1),
    type: registryItemTypeSchema,
    projectId: z.string().min(1).nullable(),
    createdBy: z.string().min(1).nullable(),
    description: z.string().nullable(),
    visibility: z.string(),
    tags: z.array(z.string()),
    category: z.string().nullable(),
    sourceType: z.string().nullable().default(null),
    sourceKey: z.string().nullable().default(null),
    shortLink: z.string().nullable().default(null),
    previewUrls: z.array(z.string()),
    previewHtml: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    files: registryContentFilesSchema,
    dependencies: z.array(z.string()),
    embeddingText: z.string(),
    embedding: z.array(z.number()),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
  })
  .describe("RegistryItemDocument");

// Create/upsert input shared by `POST /api/v1/registry/items` and the MCP
// `create_registry_item` tool. Ownership (`projectId` / `createdBy`) is
// always overridden server-side from the authenticated request context;
// the fields exist here so source artifacts can round-trip through the
// same shape.
export const registryCreateItemInputSchema = z
  .object({
    item: z
      .object({
        id: registryItemIdSchema,
        slug: registrySlugSchema,
        name: z.string().min(1),
        type: registryItemTypeSchema,
        projectId: z.string().min(1).nullable().default(null),
        createdBy: z.string().min(1).nullable().default(null),
        sourceType: z.string().min(1).max(80).nullable().default(null),
        sourceKey: z.string().min(1).max(512).nullable().default(null),
        description: z.string().nullable().default(null),
        metadata: z.record(z.string(), z.unknown()).nullable().default(null),
        // (Ezra, 2026-07-07, REPL-27571): owned channels (REST create, MCP
        // create_registry_item) may not author public items — only the internal
        // registry-catalog endpoint does — so an omitted visibility must resolve
        // to the project-scoped lane instead of silently going public.
        visibility: registryItemVisibilitySchema.default("unlisted"),
        tags: z.array(z.string()).default([]),
        category: z.string().nullable().default(null),
        previewUrls: z.array(z.string()).default([]),
        previewHtml: z.string().nullable().default(null),
        files: registryContentFilesSchema,
        dependencies: z.array(z.string()).default([]),
      })
      .superRefine(validateRegistryAppRoot),
  })
  .describe("RegistryCreateItemInput");

export const registryCreateItemOutputSchema = z
  .object({
    created: z.object({
      id: registryItemIdSchema,
      type: registryItemTypeSchema,
      slug: registrySlugSchema,
      sourceType: z.string().nullable(),
      sourceKey: z.string().nullable(),
      url: z
        .url()
        .describe(
          "Canonical public-facing URL for the new registry item. The skill returns this to the user immediately so they can share / preview without waiting on verification.",
        ),
      shareUrl: z.url().nullable(),
    }),
  })
  .describe("RegistryCreateItemOutput");
